import { BusinessInfoAISuggestionsResponse } from 'types';
import { MappedError } from 'types/common/ApiHelpers';

export type BusinessInfoAISuggestion = {
  response: BusinessInfoAISuggestionsResponse;
  websiteUrl: string;
} | null;

export type AISuggestionMap = {
  [key: string]: BusinessInfoAISuggestion;
};

export type MerchantApplicationSignUpDataModel = {
  signUpInfo: {
    firstName?: string;
    lastName?: string;
    businessEmail?: string;
    mobileNumber?: string;
    websiteUrl?: string;
    referrer?: string;
    privacyPolicyAgreement?: false;
    privacyPolicyAgreementDate?: Date;
    abn?: string;
    entityName?: string;
    entityType?: string;
    tradingName?: string;
    tradingSince?: string; // to map to DateOnly in API
    industryType?: string;
  };
};

export type MerchantApplicationContextModel = {
  signUpInfo: {
    firstName: string;
    lastName: string;
    businessEmail: string;
    mobileNumber: string;
    websiteUrl: string;
    referrer?: string;
    privacyPolicyAgreement: false;
    privacyPolicyAgreementDate: Date;
  };

  businessInfo: {
    abn?: string;
    entityName?: string;
    tradingName?: string;
    tradingSince?: string; // to map to DateOnly in API
    industryType?: string;
    entityType?: string;
  };

  paymentUseCase: {
    paymentType: string;
  };

  ecommercePlatform: {
    platform: string;
    otherValue?: string;
  };

  paymentProvider: {
    provider: string;
    otherProvider?: string;
  };

  posPlatform: {
    platform: string;
    otherPlatform?: string;
  };

  annualSales: {
    salesRange: string;
  };

  eligibilityCheck: Record<string, never>;

  userAccountCreation: Record<string, never>;

  verifyEmail: Record<string, never>;

  businessDetails: {
    address: string;
    isPartOfFranchise: boolean;
  };
};

export type StepKeyType = keyof MerchantApplicationContextModel;

export type SignUpInfoProps = MerchantApplicationContextModel['signUpInfo'];
export type BusinessInfoProps = MerchantApplicationContextModel['businessInfo'];
export type PaymentUseCaseProps =
  MerchantApplicationContextModel['paymentUseCase'];
export type EcommercePlatformProps =
  MerchantApplicationContextModel['ecommercePlatform'];
export type PaymentProviderProps =
  MerchantApplicationContextModel['paymentProvider'];
export type POSPlatformProps = MerchantApplicationContextModel['posPlatform'];
export type AnnualSalesProps = MerchantApplicationContextModel['annualSales'];

export enum PatchType {
  SignUpInfo = 'SignUpInfo',
  ActivationInfo = 'ActivationInfo',
}

export type StepDataModel = {
  patchType?: PatchType;
  merchantApplicationDetails: Partial<MerchantApplicationContextModel>;
  committedStepDetails: Partial<MerchantApplicationContextModel>;
};

export type StepComponentNavProps = {
  canGoBack?: boolean;
  canContinue?: boolean;
  isSubmitting?: boolean;
  onBack?: () => void;
  onContinue?: () => void;
  onReturnToDashboard?: () => void | Promise<void>;
};

export type StepComponentProps = {
  provideSubmit?: (
    fn: () => Promise<boolean>,
    onFieldError?: (error: MappedError) => void
  ) => void;
  onError?: (error: MappedError) => void;
  serverErrors?: Record<string, string> | null;
} & StepComponentNavProps;

export const mapToMerchantApplicationSignUpDataModel = (
  stepDetails: Record<string, unknown>,
  referrer?: string
): MerchantApplicationSignUpDataModel => {
  return {
    signUpInfo: {
      ...(stepDetails as Partial<SignUpInfoProps & BusinessInfoProps>),
      ...(referrer ? { referrer } : {}),
    },
  };
};
